import { forwardRef, useEffect, useRef, useState } from "react";
import Quill from "quill";
import "quill/dist/quill.snow.css";
import ImageResize from "quill-image-resize-module-react";

// Fonts
const Font = Quill.import("formats/font");
Font.whitelist = [
  "arial",
  "comic-sans",
  "courier-new",
  "georgia",
  "helvetica",
  "inter",
  "lucida",
  "roboto",
  "times-new-roman",
  "trebuchet-ms",
  "ubuntu",
  "verdana",
];
Quill.register(Font, true);
Quill.register("modules/imageResize", ImageResize);

// Extend Quill's Link format to force target="_blank"
const Link = Quill.import("formats/link");

class CustomLink extends Link {
  static create(value) {
    // create the node using the original Link
    const node = super.create(value);
    // sanitize the value first (use Link.sanitize)
    node.setAttribute("href", Link.sanitize(value));
    // force new tab + secure rel
    node.setAttribute("target", "_blank");
    node.setAttribute("rel", "noopener noreferrer");
    return node;
  }
}

Quill.register({ "formats/link": CustomLink }, true);

/**
 * HtmlMarkdownEditor Component
 *
 * A rich text editor built with Quill.js that supports:
 * - Custom fonts, formatting, images, videos, and hyperlinks.
 * - Image resizing with drag handles.
 * - Read-only toggle and Preview option.
 * - Access to the underlying Quill instance via forwarded ref.
 *
 * Props:
 * - defaultValue (Object): Initial Quill Delta or HTML content.
 * - onPreview (function): Callback that receives the editor's HTML when "Preview" is clicked.
 *
 * Ref:
 * - The `ref` provides direct access to the Quill instance.
 *   Example: editorRef.current.getText() or editorRef.current.root.innerHTML
 *
 * Usage Example:
 * const editorRef = useRef(null);
 *
 * <HtmlMarkdownEditor
 *    ref={editorRef}
 *    defaultValue={initialContent}
 *    onPreview={(html) => console.log("Preview content:", html)}
 * />
 *
 * // Later you can do:
 * console.log(editorRef.current.getText());
 */

const HtmlMarkdowmEditor = forwardRef(({ defaultValue, onPreview }, ref) => {
  const containerRef = useRef(null);
  const defaultValueRef = useRef(defaultValue);
  const quillRef = useRef(null);
  const [readOnly, setReadOnly] = useState(false);

  // 🔹 Modal states
  const [showModal, setShowModal] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");
  const [linkText, setLinkText] = useState("");

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const editorContainer = container.appendChild(
      container.ownerDocument.createElement("div")
    );

    const quill = new Quill(editorContainer, {
      theme: "snow",
      readOnly,
      modules: {
        toolbar: {
          container: [
            [{ font: Font.whitelist }],
            [{ size: ["small", false, "large", "huge"] }],
            ["bold", "italic", "underline", "strike"],
            [{ color: [] }, { background: [] }],
            [{ header: 1 }, { header: 2 }],
            [{ list: "ordered" }, { list: "bullet" }],
            [{ script: "sub" }, { script: "super" }],
            [{ indent: "-1" }, { indent: "+1" }],
            [{ direction: "rtl" }],
            [{ align: [] }],
            ["blockquote", "code-block"],
            ["link", "image", "video"],
            ["clean"],
          ],
          handlers: {
            link: () => {
              setShowModal(true);
            },
          },
        },
        imageResize: {
          parchment: Quill.import("parchment"),
          modules: ["Resize", "DisplaySize"],
        },
      },
    });

    if (ref && typeof ref !== "function") {
      ref.current = quill;
    }
    quillRef.current = quill;

    if (defaultValueRef.current) {
      quill.setContents(defaultValueRef.current);
    }

    return () => {
      if (ref && typeof ref !== "function") {
        ref.current = null;
      }
      container.innerHTML = "";
    };
  }, [ref, readOnly]);

  useEffect(() => {
    if (quillRef.current) {
      quillRef.current.enable(!readOnly);
    }
  }, [readOnly]);

  const handlePreview = () => {
    if (quillRef.current) {
      const html = quillRef.current.root.innerHTML;
      onPreview?.(html);
    }
  };

  const insertLink = () => {
    const quill = quillRef.current;
    if (!quill || !linkUrl) return;

    let finalUrl = linkUrl.trim();
    if (!/^https?:\/\//i.test(finalUrl)) {
      finalUrl = "https://" + finalUrl;
    }

    const range = quill.getSelection();

    if (range && range.length > 0) {
      quill.format("link", finalUrl);
    } else {
      const text = linkText || finalUrl;
      quill.insertText(range?.index || 0, text, "link", finalUrl);
      quill.setSelection((range?.index || 0) + text.length, 0);
    }

    setLinkUrl("");
    setLinkText("");
    setShowModal(false);
  };

  return (
    <>
      <style>
        {`
          /* Custom font styles for Quill */
          .ql-font-arial { font-family: Arial, sans-serif !important; }
          .ql-font-comic-sans { font-family: 'Comic Sans MS', cursive !important; }
          .ql-font-courier-new { font-family: 'Courier New', monospace !important; }
          .ql-font-georgia { font-family: Georgia, serif !important; }
          .ql-font-helvetica { font-family: Helvetica, sans-serif !important; }
          .ql-font-inter { font-family: Inter, sans-serif !important; }
          .ql-font-lucida { font-family: 'Lucida Console', monospace !important; }
          .ql-font-roboto { font-family: Roboto, sans-serif !important; }
          .ql-font-times-new-roman { font-family: 'Times New Roman', serif !important; }
          .ql-font-trebuchet-ms { font-family: 'Trebuchet MS', sans-serif !important; }
          .ql-font-ubuntu { font-family: Ubuntu, sans-serif !important; }
          .ql-font-verdana { font-family: Verdana, sans-serif !important; }

          /* Toolbar dropdown font options */
          .ql-picker.ql-font .ql-picker-label[data-value="arial"]::before,
          .ql-picker.ql-font .ql-picker-item[data-value="arial"]::before { content: 'Arial'; font-family: Arial, sans-serif; }
          .ql-picker.ql-font .ql-picker-label[data-value="comic-sans"]::before,
          .ql-picker.ql-font .ql-picker-item[data-value="comic-sans"]::before { content: 'Comic Sans MS'; font-family: 'Comic Sans MS', cursive; }
          .ql-picker.ql-font .ql-picker-label[data-value="courier-new"]::before,
          .ql-picker.ql-font .ql-picker-item[data-value="courier-new"]::before { content: 'Courier New'; font-family: 'Courier New', monospace; }
          .ql-picker.ql-font .ql-picker-label[data-value="georgia"]::before,
          .ql-picker.ql-font .ql-picker-item[data-value="georgia"]::before { content: 'Georgia'; font-family: Georgia, serif; }
          .ql-picker.ql-font .ql-picker-label[data-value="helvetica"]::before,
          .ql-picker.ql-font .ql-picker-item[data-value="helvetica"]::before { content: 'Helvetica'; font-family: Helvetica, sans-serif; }
          .ql-picker.ql-font .ql-picker-label[data-value="inter"]::before,
          .ql-picker.ql-font .ql-picker-item[data-value="inter"]::before { content: 'Inter'; font-family: Inter, sans-serif; }
          .ql-picker.ql-font .ql-picker-label[data-value="lucida"]::before,
          .ql-picker.ql-font .ql-picker-item[data-value="lucida"]::before { content: 'Lucida Console'; font-family: 'Lucida Console', monospace; }
          .ql-picker.ql-font .ql-picker-label[data-value="roboto"]::before,
          .ql-picker.ql-font .ql-picker-item[data-value="roboto"]::before { content: 'Roboto'; font-family: Roboto, sans-serif; }
          .ql-picker.ql-font .ql-picker-label[data-value="times-new-roman"]::before,
          .ql-picker.ql-font .ql-picker-item[data-value="times-new-roman"]::before { content: 'Times New Roman'; font-family: 'Times New Roman', serif; }
          .ql-picker.ql-font .ql-picker-label[data-value="trebuchet-ms"]::before,
          .ql-picker.ql-font .ql-picker-item[data-value="trebuchet-ms"]::before { content: 'Trebuchet MS'; font-family: 'Trebuchet MS', sans-serif; }
          .ql-picker.ql-font .ql-picker-label[data-value="ubuntu"]::before,
          .ql-picker.ql-font .ql-picker-item[data-value="ubuntu"]::before { content: 'Ubuntu'; font-family: Ubuntu, sans-serif; }
          .ql-picker.ql-font .ql-picker-label[data-value="verdana"]::before,
          .ql-picker.ql-font .ql-picker-item[data-value="verdana"]::before { content: 'Verdana'; font-family: Verdana, sans-serif; }

          /* Styles for preview Quill instance */
          .quill-preview-container .ql-toolbar { display: none !important; }
          .quill-preview-container .ql-container { border: none !important; }
          .quill-preview-container .ql-editor { padding: 15px !important; font-size: 16px !important; }
          
          /* Image resize handle styles */
          .ql-image-resize-handle {
            position: absolute;
            width: 8px;
            height: 8px;
            background: #4299e1;
            border: 2px solid white;
            border-radius: 50%;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
            cursor: nwse-resize;
          }
          .ql-image-resize-handle:hover { background: #2b6cb0; transform: scale(1.2); transition: all 0.2s ease; }
          .ql-image-resize-handle.ql-resize-handle-nw { top: -6px; left: -6px; cursor: nw-resize; }
          .ql-image-resize-handle.ql-resize-handle-ne { top: -6px; right: -6px; cursor: ne-resize; }
          .ql-image-resize-handle.ql-resize-handle-sw { bottom: -6px; left: -6px; cursor: sw-resize; }
          .ql-image-resize-handle.ql-resize-handle-se { bottom: -6px; right: -6px; cursor: se-resize; }
          .ql-image-display-size {
            position: absolute;
            background: rgba(0, 0, 0, 0.8);
            color: white;
            padding: 4px 8px;
            border-radius: 4px;
            font-size: 12px;
            white-space: nowrap;
            pointer-events: none;
            z-index: 1000;
          }
        `}
      </style>
      <div>
        {/* Toolbar controls */}
        <div style={{ marginBottom: "10px" }}>
          <label>
            <input
              type="checkbox"
              checked={readOnly}
              onChange={(e) => setReadOnly(e.target.checked)}
            />{" "}
            Read Only
          </label>
          <button
            style={{
              marginLeft: "15px",
              padding: "6px 14px",
              borderRadius: "6px",
              border: "1px solid #ddd",
              background: "#2563eb",
              color: "white",
              cursor: "pointer",
            }}
            onClick={handlePreview}
          >
            Preview
          </button>
        </div>

        <div ref={containerRef}></div>

        {/* 🔹 Modal */}
        {showModal && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: "rgba(0,0,0,0.5)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 9999,
            }}
          >
            <div
              style={{
                background: "white",
                padding: "20px",
                borderRadius: "8px",
                minWidth: "320px",
              }}
            >
              <h3 style={{ marginBottom: "12px" }}>Insert Hyperlink</h3>
              <input
                type="text"
                value={linkText}
                onChange={(e) => setLinkText(e.target.value)}
                placeholder="Display Text"
                style={{
                  width: "100%",
                  padding: "8px",
                  marginBottom: "10px",
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                }}
              />
              <input
                type="text"
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
                placeholder="Enter URL"
                style={{
                  width: "100%",
                  padding: "8px",
                  marginBottom: "12px",
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                }}
              />
              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <button
                  onClick={() => setShowModal(false)}
                  style={{
                    marginRight: "10px",
                    padding: "6px 12px",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                    background: "#f5f5f5",
                    cursor: "pointer",
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={insertLink}
                  style={{
                    padding: "6px 12px",
                    border: "none",
                    borderRadius: "4px",
                    background: "#2563eb",
                    color: "white",
                    cursor: "pointer",
                  }}
                >
                  Insert
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
});

HtmlMarkdowmEditor.displayName = "HtmlMarkdowmEditor";
export default HtmlMarkdowmEditor;
